import { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useCalendar } from '../../context/CalendarContext';
import { getWeekTimeRange } from '../../utils/calendar/timeCalculations';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Hook for managing calendar view state and calculations
 */
export const useCalendarView = (workingHours = [], weekStart = null) => {
    const calendarContext = useCalendar();

    // Add defensive check
    if (!calendarContext) {
        throw new Error('useCalendarView must be used within CalendarProvider');
    }

    const {
        currentDate,
        zoomLevel,
        getHourHeight,
        getWeekStart,
        getWeekEnd,
    } = calendarContext;

    // Use provided weekStart or fall back to context
    const weekStartDate = useMemo(() => {
        if (weekStart) {
            console.log('useCalendarView - using provided weekStart:', weekStart.format('YYYY-MM-DD'));
            return dayjs(weekStart).startOf('isoWeek');
        }
        const contextWeekStart = getWeekStart();
        console.log('useCalendarView - using context weekStart:', contextWeekStart.format('YYYY-MM-DD'));
        return dayjs(contextWeekStart);
    }, [weekStart, getWeekStart]);

    const weekEndDate = useMemo(() => {
        if (weekStart) {
            return dayjs(weekStart).endOf('isoWeek');
        }
        return dayjs(getWeekEnd());
    }, [weekStart, getWeekEnd]);

    const [timeRange, setTimeRange] = useState({
        earliestTime: '08:00',
        latestTime: '20:00',
        totalHours: 12,
    });

    const [visibleDateRange, setVisibleDateRange] = useState({
        start: weekStartDate,
        end: weekEndDate,
    });

    // Update time range when working hours or week changes
    useEffect(() => {
        if (workingHours.length > 0) {
            const range = getWeekTimeRange(weekStartDate, workingHours);
            setTimeRange(range);
        }
    }, [workingHours, weekStartDate]);

    // Update visible date range when week changes
    useEffect(() => {
        setVisibleDateRange({
            start: weekStartDate,
            end: weekEndDate,
        });
    }, [weekStartDate, weekEndDate]);

    // Calculate grid dimensions
    const gridDimensions = useCallback(() => {
        const hourHeight = getHourHeight();
        const totalHeight = timeRange.totalHours * hourHeight;

        return {
            hourHeight,
            totalHeight,
            timeColumnWidth: 80,
            dayColumnWidth: `calc((100% - 80px) / 7)`,
        };
    }, [getHourHeight, timeRange.totalHours]);

    // Get visible hours array
    const getVisibleHours = useCallback(() => {
        const hours = [];
        const [startHour] = timeRange.earliestTime.split(':').map(Number);
        const [endHour] = timeRange.latestTime.split(':').map(Number);

        for (let hour = startHour; hour <= endHour; hour++) {
            hours.push({
                hour,
                label: `${hour.toString().padStart(2, '0')}:00`,
                position: (hour - startHour) * getHourHeight(),
            });
        }

        return hours;
    }, [timeRange, getHourHeight]);

    // Get days of the week
    const getDaysOfWeek = useCallback(() => {
        const days = [];
        console.log('getDaysOfWeek - weekStartDate:', weekStartDate.format('YYYY-MM-DD'));

        for (let i = 0; i < 7; i++) {
            const day = weekStartDate.add(i, 'day');
            days.push({
                date: day,
                dayName: day.format('ddd'),
                dayNumber: day.format('D'),
                monthName: day.format('MMM'),
                isToday: day.isSame(dayjs(), 'day'),
                isWeekend: day.day() === 0 || day.day() === 6,
                dayOfWeek: ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][day.day()],
            });
        }

        return days;
    }, [weekStartDate]);

    // Check if a date is in the visible range
    const isDateVisible = useCallback((date) => {
        if (!date) return false;
        const d = dayjs(date);
        const rangeStart = dayjs(visibleDateRange.start);
        const rangeEnd = dayjs(visibleDateRange.end);

        return d.isValid() &&
            rangeStart.isValid() &&
            rangeEnd.isValid() &&
            d.isSameOrAfter(rangeStart, 'day') &&
            d.isSameOrBefore(rangeEnd, 'day');
    }, [visibleDateRange]);

    // Scroll to current time
    const scrollToCurrentTime = useCallback((containerRef) => {
        if (!containerRef?.current) return;

        const now = dayjs();
        const [startHour] = timeRange.earliestTime.split(':').map(Number);
        const currentHour = now.hour();
        const currentMinute = now.minute();

        if (currentHour >= startHour) {
            const hoursSinceStart = currentHour - startHour;
            const minuteOffset = currentMinute / 60;
            const scrollPosition = (hoursSinceStart + minuteOffset) * getHourHeight();

            // Scroll to position with some offset to center the current time
            containerRef.current.scrollTop = Math.max(0, scrollPosition - 100);
        }
    }, [timeRange.earliestTime, getHourHeight]);

    // Get current time indicator position
    const getCurrentTimePosition = useCallback(() => {
        const now = dayjs();
        const [startHour] = timeRange.earliestTime.split(':').map(Number);
        const nowHour = now.hour();
        const nowMinute = now.minute();

        if (nowHour < startHour) return null;

        const hoursSinceStart = nowHour - startHour + (nowMinute / 60);
        const position = hoursSinceStart * getHourHeight();

        return {
            top: position,
            time: now.format('HH:mm'),
            visible: now.isValid() && isDateVisible(now),
        };
    }, [timeRange.earliestTime, getHourHeight, isDateVisible]);

    return {
        timeRange,
        visibleDateRange,
        gridDimensions: gridDimensions(),
        visibleHours: getVisibleHours(),
        daysOfWeek: getDaysOfWeek(),
        isDateVisible,
        scrollToCurrentTime,
        currentTimePosition: getCurrentTimePosition(),
        weekStartDate, // Export this for debugging
    };
};